import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/CrudModule";
import { StatusBadge } from "@/components/StatusBadge";
import { useReference } from "@/context/ReferenceContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const EMPTY = {
  client_name: "", phone: "", state: "", product_goal: "",
  target_monthly_budget: "", target_face_amount: "", lead_source: "",
  lead_cost: "", assigned_agent: "", health_intake_complete: false,
  handoff_status: "Pending Review", notes: "",
};

export default function IntakeQueue() {
  const { opts } = useReference();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);

  const load = () => {
    api.get("/leads").then(({ data }) => {
      const intake = data
        .filter((l) => ["New Lead", "Contacted", "Fact Find Complete"].includes(l.stage))
        .slice(0, 12);
      setRecent(intake);
    });
  };
  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const handoffReady = form.handoff_status === "Handed Off to Agent";
      await api.post("/leads", {
        ...form,
        bpo_caller: "Jordan Lee",
        date_received: new Date().toISOString().slice(0, 10),
        stage: form.health_intake_complete ? "Fact Find Complete" : "New Lead",
        next_action: handoffReady ? "Agent to prepare quote" : "Complete fact find",
        next_action_date: new Date().toISOString().slice(0, 10),
        last_activity: new Date().toISOString().slice(0, 10),
      });
      toast.success("Lead added to pipeline");
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const Sel = ({ k, label, options, placeholder }) => (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Select value={form[k]} onValueChange={(v) => set(k, v)}>
        <SelectTrigger data-testid={`intake-${k}`}><SelectValue placeholder={placeholder || "Select…"} /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  return (
    <div>
      <PageHeader title="Intake Queue" subtitle="BPO fact-find & underwriting intake. Hand off to a licensed agent." />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <form onSubmit={submit} className="xl:col-span-2 rounded-lg border border-border bg-card p-5" data-testid="intake-form">
          <div className="mb-4 flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-primary" />
            <h3 className="font-heading text-base font-semibold">New Lead Intake</h3>
          </div>

          <div className="overline mb-3">Contact Information</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Client Name *</Label>
              <Input data-testid="intake-client_name" value={form.client_name} onChange={(e) => set("client_name", e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Phone</Label>
              <Input data-testid="intake-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
            <Sel k="state" label="State" options={opts.states} />
            <Sel k="product_goal" label="Product Interest" options={opts.products} />
          </div>

          <div className="overline mb-3 mt-6">Coverage Goals</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Desired Monthly Budget</Label>
              <Input type="number" data-testid="intake-budget" value={form.target_monthly_budget} onChange={(e) => set("target_monthly_budget", Number(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Desired Coverage Amount</Label>
              <Input type="number" data-testid="intake-coverage" value={form.target_face_amount} onChange={(e) => set("target_face_amount", Number(e.target.value))} />
            </div>
          </div>

          <div className="overline mb-3 mt-6">Lead Origin</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Sel k="lead_source" label="Lead Source" options={opts.sources} />
            <div className="space-y-1.5">
              <Label className="text-xs">Lead Cost</Label>
              <Input type="number" data-testid="intake-lead_cost" value={form.lead_cost} onChange={(e) => set("lead_cost", Number(e.target.value))} />
            </div>
            <Sel k="assigned_agent" label="Assign to Agent" options={opts.agents} />
            <Sel k="handoff_status" label="Handoff Status" options={["Pending Review", "Needs More Info", "Handed Off to Agent"]} />
          </div>

          <div className="mt-6 flex items-center justify-between rounded-md border border-border px-3 py-2.5">
            <div>
              <Label className="text-sm">Fact-Find / Underwriting Complete</Label>
              <p className="text-xs text-muted-foreground">Toggle when basic health intake is done.</p>
            </div>
            <Switch data-testid="intake-health" checked={form.health_intake_complete} onCheckedChange={(c) => set("health_intake_complete", c)} />
          </div>

          <div className="mt-4 space-y-1.5">
            <Label className="text-xs">Notes for Licensed Agent</Label>
            <Textarea data-testid="intake-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Health conditions, medications, best call times, special requests…" />
          </div>

          <div className="mt-5 flex justify-end">
            <Button type="submit" disabled={saving} data-testid="intake-submit">
              <Send className="mr-1.5 h-4 w-4" /> {saving ? "Submitting…" : "Submit to Pipeline"}
            </Button>
          </div>
        </form>

        <div>
          <div className="overline mb-3">Recently Intaked</div>
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                  <TableHead className="overline">Client</TableHead>
                  <TableHead className="overline">Stage</TableHead>
                  <TableHead className="overline">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent.length === 0 && (
                  <TableRow><TableCell colSpan={3} className="h-20 text-center text-muted-foreground text-sm">No recent intake.</TableCell></TableRow>
                )}
                {recent.map((r) => (
                  <TableRow key={r.id} className="text-[13px]">
                    <TableCell className="font-medium">{r.client_name}</TableCell>
                    <TableCell><StatusBadge value={r.stage} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(r.date_received)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
